import { supabase, checkSupabaseHealth } from './supabaseClient';
// Browser-compatible hash function
const createSimpleHash = (input) => {
    let hash = 0;
    if (input.length === 0) return hash.toString();
    for (let i = 0; i < input.length; i++) {
        const char = input.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
};

// Get session ID for anonymous users (same pattern as savedTranslationsService)
const getSessionId = () => {
    const newKey = 'lexory_session_id';
    const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';

    let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
    if (!sessionId) {
        sessionId = require('uuid').v4();
    }

    localStorage.setItem(newKey, sessionId);
    return sessionId;
};

// Generate unique document hash for identification
const generateDocumentHash = (fileName, fileSize, fileType) => {
    const hashInput = `${fileName}_${fileSize}_${fileType}_${Date.now()}`;
    return createSimpleHash(hashInput);
};

// Save user's reading progress
export const saveReadingProgress = async (progressData, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        const progressRecord = {
            user_id: user?.id || null,
            session_id: user ? null : getSessionId(),
            document_hash: progressData.documentHash,
            document_name: progressData.documentName,
            document_type: progressData.documentType,
            current_page: progressData.currentPage || 1,
            total_pages: progressData.totalPages || 1,
            scroll_position: progressData.scrollPosition || 0,
            progress_percentage: progressData.progressPercentage || 0,
            reading_time_minutes: progressData.readingTimeMinutes || 0,
            last_accessed: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };

        // Check if progress already exists for this document
        let query = supabase
            .from('user_progress')
            .select('id')
            .eq('document_hash', progressData.documentHash);

        if (user) {
            query = query.eq('user_id', user.id);
        } else {
            query = query.eq('session_id', getSessionId());
        }

        const { data: existing, error: selectError } = await query;

        if (selectError) {
            throw selectError;
        }

        let result;
        if (existing && existing.length > 0) {
            // Update existing progress
            const { data, error } = await supabase
                .from('user_progress')
                .update({
                    current_page: progressRecord.current_page,
                    scroll_position: progressRecord.scroll_position,
                    progress_percentage: progressRecord.progress_percentage,
                    reading_time_minutes: progressRecord.reading_time_minutes,
                    last_accessed: progressRecord.last_accessed,
                    updated_at: progressRecord.updated_at
                })
                .eq('id', existing[0].id)
                .select();

            result = { data, error };
        } else {
            // Insert new progress record
            const { data, error } = await supabase
                .from('user_progress')
                .insert([progressRecord])
                .select();

            result = { data, error };
        }

        if (result.error) {
            throw result.error;
        }

        return { data: result.data, error: null };
    } catch (error) {
        console.error('Error saving reading progress:', error);
        return { data: null, error: error.message };
    }
};

// Fetch user's reading progress
export const fetchReadingProgress = async (user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        let query = supabase
            .from('user_progress')
            .select('*')
            .order('last_accessed', { ascending: false });

        // Filter by user if authenticated, otherwise by session
        if (user) {
            query = query.eq('user_id', user.id);
        } else {
            const sessionId = getSessionId();
            query = query.eq('session_id', sessionId);
        }

        const { data, error } = await query;

        if (error) {
            throw error;
        }

        return { data: data || [], error: null };
    } catch (error) {
        console.error('Error fetching reading progress:', error);
        return { data: [], error: error.message };
    }
};

// Get progress for a specific document
export const getDocumentProgress = async (documentHash, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        let query = supabase
            .from('user_progress')
            .select('*')
            .eq('document_hash', documentHash);

        if (user) {
            query = query.eq('user_id', user.id);
        } else {
            const sessionId = getSessionId();
            query = query.eq('session_id', sessionId);
        }

        const { data, error } = await query.maybeSingle();

        if (error) {
            throw error;
        }

        return { data: data || null, error: null };
    } catch (error) {
        console.error('Error fetching document progress:', error);
        return { data: null, error: error.message };
    }
};

// Get most recent reading session (for "Continue Reading" feature)
export const getLastReadingSession = async (user = null) => {
    try {
        const { data: progressList, error } = await fetchReadingProgress(user);
        
        if (error) {
            throw new Error(error);
        }

        // Return the most recently accessed document
        return { 
            data: progressList && progressList.length > 0 ? progressList[0] : null, 
            error: null 
        };
    } catch (error) {
        console.error('Error fetching last reading session:', error);
        return { data: null, error: error.message };
    }
};

// Delete progress for a specific document
export const deleteDocumentProgress = async (documentHash, user = null) => {
    try {
        const isHealthy = await checkSupabaseHealth();
        if (!isHealthy) {
            throw new Error('Database connection unavailable');
        }

        let query = supabase
            .from('user_progress')
            .delete()
            .eq('document_hash', documentHash);

        if (user) {
            query = query.eq('user_id', user.id);
        } else {
            const sessionId = getSessionId();
            query = query.eq('session_id', sessionId);
        }

        const { data, error } = await query;

        if (error) {
            throw error;
        }

        return { data, error: null };
    } catch (error) {
        console.error('Error deleting document progress:', error);
        return { data: null, error: error.message };
    }
};

// Utility functions for document identification
export const createDocumentIdentifier = (file) => {
    if (!file) return null;
    
    return {
        hash: generateDocumentHash(file.name, file.size, file.type),
        name: file.name,
        type: file.type.includes('pdf') ? 'pdf' : 'text'
    };
};

// Auto-save progress with debouncing
let autoSaveTimeout = null;
export const autoSaveProgress = (progressData, user = null, delay = 30000) => {
    // Clear existing timeout
    if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
    }

    // Set new timeout for auto-save
    autoSaveTimeout = setTimeout(async () => {
        try {
            await saveReadingProgress(progressData, user);
            console.log('Progress auto-saved successfully');
        } catch (error) {
            console.error('Auto-save failed:', error);
        }
    }, delay);
};

// Clear auto-save timeout (call on component unmount)
export const clearAutoSave = () => {
    if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
        autoSaveTimeout = null;
    }
};
